import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Printer } from "lucide-react";
import { billingApi, billingQueryKeys } from "../../shared/api/billing";
import {
  formatDate,
  formatDateTime,
  formatPhone,
  formatVndWithUnit,
  paymentMethodLabel,
} from "../../shared/format";
import { useClinicHeader } from "../../shared/hooks/useClinicHeader";
import { ErrorState } from "../../shared/ui/ErrorState";
import { Skeleton } from "../../shared/ui/Skeleton";
import type { VatCategory } from "../../shared/types/billing";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const VAT_LABEL: Record<VatCategory, string> = {
  KCT: "KCT",
  VAT_0: "0%",
  VAT_5: "5%",
  VAT_8: "8%",
  VAT_10: "10%",
};

/**
 * Who signs "Người thu" (#19170, L4): the staff member who took the money, not whoever created the
 * invoice — at a desk those are often two people. A receipt covering several collections is signed
 * by the latest one; each collection and its cashier are already listed above.
 */
export function receiptCashier(
  payments: ReadonlyArray<{ receivedAt: string | null; cashierName: string | null }>,
): string | null {
  const named = payments.filter((p) => p.cashierName && p.cashierName.trim());
  if (named.length === 0) return null;
  const latest = named.reduce((a, b) =>
    (b.receivedAt ?? "") > (a.receivedAt ?? "") ? b : a,
  );
  return latest.cashierName!.trim();
}

export function InvoiceReceiptPage() {
  const { id = "" } = useParams<{ id: string }>();

  const detail = useQuery({
    queryKey: billingQueryKeys.invoiceDetail(id),
    queryFn: () => billingApi.get(id),
    enabled: Boolean(id),
  });
  // Cài đặt → Phòng khám of the signed-in tenant (#19168). Was a hard-coded sample clinic.
  const clinic = useClinicHeader();

  if (detail.isLoading) {
    return (
      <div className="p-4">
        <Skeleton height={500} />
      </div>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <div className="p-4">
        <ErrorState
          error={detail.error ?? new Error("Không tìm thấy hoá đơn.")}
          title="Không tải được hoá đơn"
          onRetry={() => detail.refetch()}
        />
      </div>
    );
  }

  const inv = detail.data;

  return (
    // WYSIWYG (#18542): a single fixed A4-content width used both on screen and in
    // print, so the Review reflects exactly what prints. Without this the on-screen
    // (max-w-3xl) and print (max-w-none) widths differed → header wrapped differently
    // and the content column was squeezed only when printing.
    <div className="mx-auto w-full max-w-[680px] p-4 font-sans print:p-0">
      {/* #18881 — this route renders inside the app shell, so printing it put the sidebar and
          topbar on a document handed to a customer. `print:hidden` on this page's own toolbar
          cannot reach the shell, which is a sibling of the router outlet; isolating the sheet by
          visibility is how EncounterPrintPage and PrescriptionPrintPage already do it. Kept as a
          style block because @page and the isolation rule have no Tailwind utility. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          body * { visibility: hidden; }
          .receipt-print, .receipt-print * { visibility: visible; }
          .receipt-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; }
        }
      `}</style>
      <div className="mb-4 flex items-center justify-between gap-2 print:hidden">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/invoices/${inv.id}`}>
            <ArrowLeft aria-hidden="true" />
            Quay lại hoá đơn
          </Link>
        </Button>
        {/* Disabled until the clinic header is there, so a receipt cannot go out without it. */}
        <Button type="button" onClick={() => window.print()} disabled={!clinic.header}>
          <Printer aria-hidden="true" />
          In biên lai
        </Button>
      </div>

      {clinic.isError && (
        <p className="mb-3 text-sm text-destructive print:hidden" role="alert">
          Không tải được thông tin phòng khám (Cài đặt → Phòng khám). Tải lại trang trước khi in biên
          lai.
        </p>
      )}
      <article className="receipt-print rounded-md border bg-card p-6 text-sm text-foreground shadow-sm print:rounded-none print:border-0 print:shadow-none">
        <header className="flex flex-nowrap items-start justify-between gap-4 border-b pb-4">
          <div className="min-w-0 space-y-0.5">
            {clinic.header && (
              <>
                <h2 className="text-lg font-bold" data-testid="clinic-name">
                  {clinic.header.name}
                </h2>
                {clinic.header.address && (
                  <div className="text-muted-foreground">{clinic.header.address}</div>
                )}
                {clinic.header.phoneAndTax && (
                  <div className="text-muted-foreground">{clinic.header.phoneAndTax}</div>
                )}
                {clinic.header.email && (
                  <div className="text-muted-foreground">{clinic.header.email}</div>
                )}
              </>
            )}
          </div>
          <div className="shrink-0 text-right">
            <h1 className="text-xl font-extrabold uppercase tracking-tight">
              Biên lai thu tiền
            </h1>
            <div className="font-mono text-base">{inv.code}</div>
            <div className="text-muted-foreground">Ngày: {formatDate(inv.issuedAt)}</div>
          </div>
        </header>

        <section className="grid gap-1.5 py-4 sm:grid-cols-2">
          <div className="flex flex-wrap gap-x-2">
            <span className="text-muted-foreground">Họ và tên bệnh nhân:</span>
            <strong>{inv.patientName}</strong>
          </div>
          <div className="flex flex-wrap gap-x-2">
            <span className="text-muted-foreground">SĐT:</span>
            <span className="font-mono">
              {inv.patientPhone ? formatPhone(inv.patientPhone) : "—"}
            </span>
          </div>
          {inv.doctorName && (
            <div className="flex flex-wrap gap-x-2">
              <span className="text-muted-foreground">Bác sĩ phụ trách:</span>
              <span>{inv.doctorName}</span>
            </div>
          )}
        </section>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">#</TableHead>
              <TableHead>Nội dung</TableHead>
              <TableHead className="w-10">ĐVT</TableHead>
              <TableHead className="w-12 text-right">SL</TableHead>
              <TableHead className="w-28 text-right">Đơn giá</TableHead>
              <TableHead className="w-12 text-right">VAT</TableHead>
              <TableHead className="w-32 text-right">Thành tiền</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {inv.items.map((it, idx) => (
              <TableRow key={it.id}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{it.name}</TableCell>
                <TableCell className="font-mono">{it.unit ?? "—"}</TableCell>
                <TableCell className="text-right">{it.quantity}</TableCell>
                <TableCell className="text-right font-mono">
                  {formatVndWithUnit(it.unitPrice)}
                </TableCell>
                <TableCell className="text-right">
                  {it.vatCategory ? VAT_LABEL[it.vatCategory] : "—"}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {formatVndWithUnit(it.lineTotal)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <section className="ml-auto mt-4 w-full max-w-xs space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Tạm tính</span>
            <span className="font-mono">{formatVndWithUnit(inv.subtotal)}</span>
          </div>
          {inv.discountTotal > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Giảm giá</span>
              <span className="font-mono">−{formatVndWithUnit(inv.discountTotal)}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">VAT</span>
            <span className="font-mono">{formatVndWithUnit(inv.vatTotal)}</span>
          </div>
          <div className="flex items-center justify-between border-t pt-1.5 text-base font-bold">
            <span>TỔNG CỘNG</span>
            <span>{formatVndWithUnit(inv.grandTotal)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Đã thu</span>
            <span className="font-mono">{formatVndWithUnit(inv.paidTotal)}</span>
          </div>
          {inv.balanceDue > 0 && (
            <div className="flex items-center justify-between font-semibold text-destructive">
              <span>Còn phải thu</span>
              <span>{formatVndWithUnit(inv.balanceDue)}</span>
            </div>
          )}
        </section>

        {inv.payments.length > 0 && (
          <section className="mt-6">
            <h4 className="mb-2 font-semibold">Thanh toán</h4>
            <ul className="space-y-1">
              {inv.payments.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-b py-1 last:border-0"
                >
                  <span className="text-muted-foreground">{formatDateTime(p.receivedAt)}</span>
                  <span>{paymentMethodLabel[p.method]}</span>
                  {p.reference && <span className="font-mono">{p.reference}</span>}
                  <span className="ml-auto font-mono">{formatVndWithUnit(p.amount)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {inv.note && (
          <section className="mt-6">
            <strong>Ghi chú:</strong> {inv.note}
          </section>
        )}

        <footer className="mt-8 grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="font-medium">Bệnh nhân</div>
            <div className="text-xs italic text-muted-foreground">(ký, ghi rõ họ tên)</div>
          </div>
          <div>
            <div className="font-medium">Người thu</div>
            <div className="mt-6 border-t pt-1 text-sm" data-testid="receipt-cashier">
              {receiptCashier(inv.payments) ?? ""}
            </div>
          </div>
        </footer>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Cảm ơn quý khách. Vui lòng giữ biên lai để đối chiếu khi tái khám.
        </p>
      </article>
    </div>
  );
}
